import { productApis } from "@payping/api";
import type { ProductListItemWithImageViewModel } from "@payping/api";
import { ensureString } from "@payping/utilities-shared/string";
import {
  Checkbox,
  Col,
  LegacyCardList,
  LegacyForm,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from "@payping/web-components";
import React, { ReactElement, useState } from "react";
import { useAsyncFn, useDebounce } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { DisplayModeEnum } from "@payping/web-components/legacyComponents/List/types";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";
import styles from "@/assets/scss/Shop/Product/form.module.scss";

interface Props {
  loading?: boolean;
  productList: ProductListItemWithImageViewModel[];
  setProductList: React.Dispatch<React.SetStateAction<ProductListItemWithImageViewModel[]>>;
}

const ProductSelect: React.FC<Props> = (props: Props) => {
  const { productList, setProductList, loading, ...otherProps } = props;
  const [searchText, setSearchText] = useState<string>("");
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductListItemWithImageViewModel[]>([]);

  const { t } = useAppTranslation();
  const debounceTimeout = 1000;
  const limit = 10;

  const [{ value: countValue }, getCount] = useAsyncFn(
    () => productApis.productGetListCount(false, searchText),
    [searchText],
  );

  const data: ListCardProps["dataSource"] = selectedProducts?.map((selectedp, index) => ({
    key: index,
    image: { type: "image", src: selectedp.imageLink || "" },
    actions: [
      <Space key={index} size={"middle"}>
        <Typography.Link
          onClick={() =>
            setSelectedProducts(selectedProducts?.filter((selectedProduct) => selectedProduct.code !== selectedp.code))
          }
        >
          {t("removeFromList")}
        </Typography.Link>
      </Space>,
    ],
    descriptions: <Typography.Title level={5}>{selectedp.title}</Typography.Title>,
  }));

  const [, fetchOptions] = useAsyncFn(
    async (restart?: boolean) => {
      const res = await productApis.productGetListWithImage(restart ? 0 : productList.length, limit, false, searchText);
      setProductList((prev) => (restart ? res : [...prev, ...res]));
      setLoadingProducts(false);
      return res;
    },
    [searchText, productList.length, setProductList],
  );

  useDebounce(
    async () => {
      await fetchOptions(true);
      await getCount();
    },
    debounceTimeout,
    [searchText],
  );

  return (
    <Space orientation={"vertical"} className={"tw:w-full"}>
      <Row justify={"space-between"} gutter={[16, 0]}>
        <Col span={12}>
          <LegacyForm.Item shouldUpdate={(prev, curr): boolean => prev.allProducts !== curr.allProducts}>
            {(form1): ReactElement => {
              const val = form1.getFieldValue("allProducts");
              return (
                <LegacyForm.Item
                  label={t("product.titleP")}
                  name={"activeProductCode"}
                  rules={val ? undefined : [{ required: true }]}
                >
                  <Select
                    disabled={val || loading}
                    mode={"multiple"}
                    className={"tw:w-full"}
                    allowClear
                    filterOption={false}
                    onSelect={(selectedCode) => {
                      setSelectedProducts((prevState) => {
                        const s = productList.find((product) => product.code === selectedCode);
                        if (s) return [...prevState, s];
                        return [...prevState];
                      });
                    }}
                    onDeselect={(deselectedCode) => {
                      setSelectedProducts(
                        selectedProducts?.filter((selectedProduct) => selectedProduct.code !== deselectedCode),
                      );
                    }}
                    popupRender={(menu) => (
                      <div>
                        {loadingProducts ? (
                          <Spin size="small" />
                        ) : (
                          <div>
                            {menu}
                            {countValue?.count && countValue.count > productList.length ? (
                              <div
                                className={styles.more}
                                onClick={async () => {
                                  setLoadingProducts(true);
                                  await fetchOptions();
                                }}
                              >
                                {t("more")}...
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                    onSearch={(value) => {
                      setLoadingProducts(true);
                      setSearchText(value);
                    }}
                    options={productList.map((p) => ({
                      label: p.title,
                      title: p.title,
                      value: ensureString({ text: p.code ?? "", fallbackValue: "-" }),
                    }))}
                    {...otherProps}
                  />
                </LegacyForm.Item>
              );
            }}
          </LegacyForm.Item>
        </Col>
        <Col span={12} className={"tw:align-center tw:flex"}>
          <LegacyForm.Item name={"allProducts"} valuePropName={"checked"}>
            <Space>
              {t("coupon.form.activeForAll")}
              <Checkbox />
            </Space>
          </LegacyForm.Item>
        </Col>
      </Row>

      <LegacyCardList
        bordered={false}
        className={"tw:w-full tw:border-none"}
        mode={DisplayModeEnum.List}
        grid={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 4, xxl: 4, xxxl: 4 }}
        dataSource={data}
      />
    </Space>
  );
};

export default ProductSelect;
